import {
  AutoAwesome,
  Brush,
  Close,
  CloudUpload,
  Collections,
  Delete,
  Image as ImageIcon,
  Info,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Card,
  CardMedia,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import {
  BRAND_IMAGE_FIELD_LIMITS,
  BRAND_IMAGE_TYPE_BANNER,
  BRAND_IMAGE_TYPE_CHARACTER,
  BRAND_IMAGE_TYPE_HERO,
  BRAND_IMAGE_TYPE_ICON,
  BRAND_IMAGE_TYPE_LOGO,
  BRAND_IMAGE_TYPE_OTHER,
  BRAND_IMAGE_TYPE_PRODUCT,
  BRAND_IMAGE_TYPE_SOCIAL,
} from "../../constants";
import { v4 as uuidv4 } from "uuid";
import { Company } from "../../types";
import { hostname, parseError } from "../../utils";
import FrameBuilderDialog from "./dialogs/FrameBuilderDialog";
import ImagePicker from "../../components/ImagePicker";

interface BrandImageDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  company: Company;
  onSave: (imageData: any) => Promise<void>;
  existingData?: any;
  loading: boolean;
  getToken: Function;
  eventListener: Function;
}

const IMAGE_TYPES = [
  { value: BRAND_IMAGE_TYPE_LOGO, label: "Logo" },
  { value: BRAND_IMAGE_TYPE_PRODUCT, label: "Product" },
  { value: BRAND_IMAGE_TYPE_HERO, label: "Hero" },
  { value: BRAND_IMAGE_TYPE_ICON, label: "Icon" },
  { value: BRAND_IMAGE_TYPE_BANNER, label: "Banner" },
  { value: BRAND_IMAGE_TYPE_SOCIAL, label: "Social Media" },
  { value: BRAND_IMAGE_TYPE_CHARACTER, label: "Character" },
  { value: BRAND_IMAGE_TYPE_OTHER, label: "Other" },
];

const BrandImageDialog: React.FC<BrandImageDialogProps> = ({
  open,
  onClose,
  companyId,
  company,
  onSave,
  existingData,
  loading,
  getToken,
  eventListener,
}) => {
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    description: "",
    imageStylePrompt: "",
    images: [] as any[],
    imageModels: [] as string[],
    templates: [] as any[],
  });
  const [uploading, setUploading] = useState(false);
  const [availableImageModels, setAvailableImageModels] = useState<any[]>([]);
  const [stylePromptInput, setStylePromptInput] = useState("");
  const [generatingStylePrompt, setGeneratingStylePrompt] = useState(false);
  const [frameBuilderOpen, setFrameBuilderOpen] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  // Fetch available image models from API
  const fetchImageModels = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/api/v0/images/models`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAvailableImageModels(response.data);
    } catch (err) {
      console.error("Failed to fetch image models:", err);
      // Set a default fallback if API fails
      setAvailableImageModels([
        {
          id: "gpt-image-1",
          name: "GPT Image 1",
          tags: ["Best", "Image Ref"],
        },
      ]);
    }
  };

  useEffect(() => {
    if (open) {
      fetchImageModels();
    }
  }, [open]);

  useEffect(() => {
    if (existingData) {
      // Filter imageModels to only include valid models from availableImageModels
      const existingModels = existingData.imageModels || [];
      const validModelIds = new Set(availableImageModels.map((m) => m.id));
      const filteredModels = existingModels.filter((modelId: string) =>
        validModelIds.has(modelId)
      );

      // Log if any models were removed
      if (filteredModels.length !== existingModels.length) {
        const removedModels = existingModels.filter(
          (modelId: string) => !validModelIds.has(modelId)
        );
        console.log(
          "Removed invalid/deprecated image models:",
          removedModels
        );
      }

      setFormData({
        id: existingData.id || "",
        name: existingData.name || "",
        description: existingData.description || "",
        imageStylePrompt: existingData.imageStylePrompt || "",
        images: existingData.images || [],
        imageModels: filteredModels,
        templates: existingData.templates || [],
      });
    } else {
      // Reset form for new image
      setFormData({
        id: "",
        name: "",
        description: "",
        imageStylePrompt: "",
        images: [],
        imageModels: [],
        templates: [],
      });
    }
    setStylePromptInput("");
  }, [existingData, open, availableImageModels]);

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const uploadedImages: any[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Validate file type
        if (!file.type.startsWith("image/")) {
          eventListener({
            type: "SET_ERROR",
            error: `${file.name} is not a valid image file`,
          });
          continue;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          eventListener({
            type: "SET_ERROR",
            error: `${file.name} is too large (max 10MB)`,
          });
          continue;
        }

        // Create FormData for file upload to platform-images
        const uploadData = new FormData();
        uploadData.append("image", file);
        uploadData.append("name", file.name);
        uploadData.append("category", "brand_image");
        uploadData.append("companyId", companyId);
        uploadData.append("public", "true");

        const token = await getToken();
        const response = await axios.post(
          `${hostname}/platform-images`,
          uploadData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data && response.data.url) {
          uploadedImages.push({
            id: response.data.id,
            url: response.data.url,
            width: response.data.width,
            height: response.data.height,
            fileSize: response.data.fileSize,
            mimeType: response.data.mimeType,
            type: "",
          });
        }
      }

      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...uploadedImages],
      }));
    } catch (error) {
      console.error("Error uploading images:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleImageTypeChange = (index: number, type: string) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.map((img, i) =>
        i === index ? { ...img, type } : img
      ),
    }));
  };

  const handleImageDescriptionChange = (index: number, description: string) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.map((img, i) =>
        i === index ? { ...img, description } : img
      ),
    }));
  };

  const handleModelToggle = (modelId: string) => {
    setFormData((prev) => ({
      ...prev,
      imageModels: prev.imageModels.includes(modelId)
        ? prev.imageModels.filter((id) => id !== modelId)
        : [...prev.imageModels, modelId],
    }));
  };

  const handleTemplateUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const uploadedTemplates: any[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Validate file type - templates must be PNG for transparency
        if (file.type !== "image/png") {
          eventListener({
            type: "SET_ERROR",
            error: `${file.name} must be a PNG file. Templates require PNG format for transparency.`,
          });
          continue;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          eventListener({
            type: "SET_ERROR",
            error: `${file.name} is too large (max 10MB)`,
          });
          continue;
        }

        // Create FormData for file upload to platform-images
        const uploadData = new FormData();
        uploadData.append("image", file);
        uploadData.append("name", file.name);
        uploadData.append("category", "brand_template");
        uploadData.append("companyId", companyId);
        uploadData.append("public", "true");

        const token = await getToken();
        const response = await axios.post(
          `${hostname}/platform-images`,
          uploadData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data && response.data.url) {
          uploadedTemplates.push({
            id: response.data.id || uuidv4(),
            url: response.data.url,
            name: file.name.replace(/\.png$/i, ""),
            description: "",
            width: response.data.width,
            height: response.data.height,
          });
        }
      }

      setFormData((prev) => ({
        ...prev,
        templates: [...prev.templates, ...uploadedTemplates],
      }));
    } catch (error) {
      console.error("Error uploading templates:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveTemplate = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      templates: prev.templates.filter((_, i) => i !== index),
    }));
  };

  const handleTemplateNameChange = (index: number, name: string) => {
    setFormData((prev) => ({
      ...prev,
      templates: prev.templates.map((tmpl, i) =>
        i === index ? { ...tmpl, name } : tmpl
      ),
    }));
  };

  const handleTemplateDescriptionChange = (
    index: number,
    description: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      templates: prev.templates.map((tmpl, i) =>
        i === index ? { ...tmpl, description } : tmpl
      ),
    }));
  };

  const handleGenerateStylePrompt = async () => {
    setGeneratingStylePrompt(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/content/brand-images/generate-style-prompt`,
        {
          userPrompt: stylePromptInput,
          companyId: companyId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.stylePrompt) {
        setFormData((prev) => ({
          ...prev,
          imageStylePrompt: response.data.stylePrompt,
        }));
      }
    } catch (error: any) {
      console.error("Error generating style prompt:", error);

      if (error.response?.status === 429) {
        const errorData = error.response.data;
        const message = `Usage limit exceeded. You've used ${errorData.current_usage} out of ${errorData.credit_limit} credits this month.`;
        eventListener({
          type: "SET_ERROR",
          error: message,
        });
      } else {
        eventListener({
          type: "SET_ERROR",
          error: parseError(error),
        });
      }
    } finally {
      setGeneratingStylePrompt(false);
    }
  };

  const handleImagePickerSelect = async (imageUrl: string, imageId?: string) => {
    // Add the selected image to the brand images
    // Try to get image dimensions
    const img = new Image();
    img.onload = () => {
      const newImage = {
        id: imageId || uuidv4(),
        url: imageUrl,
        width: img.width,
        height: img.height,
        type: "",
        description: "",
      };
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, newImage],
      }));
    };
    img.onerror = () => {
      // If we can't load the image dimensions, add it without them
      const newImage = {
        id: imageId || uuidv4(),
        url: imageUrl,
        type: "",
        description: "",
      };
      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, newImage],
      }));
    };
    img.src = imageUrl;
  };

  const handleSave = async () => {
    if (!formData.name) {
      eventListener({
        type: "SET_ERROR",
        error: "Please provide a name for the brand image",
      });
      return;
    }

    await onSave(formData);
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "85vh",
        },
      }}
    >
      <DialogTitle>
        {existingData ? "Edit Brand Image" : "New Brand Image"}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Name */}
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
            inputProps={{ maxLength: BRAND_IMAGE_FIELD_LIMITS.name }}
            helperText={`${formData.name?.length || 0}/${BRAND_IMAGE_FIELD_LIMITS.name} characters`}
          />

          {/* Description */}
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            margin="normal"
            multiline
            rows={4}
            inputProps={{ maxLength: BRAND_IMAGE_FIELD_LIMITS.description }}
            helperText={`${formData.description?.length || 0}/${BRAND_IMAGE_FIELD_LIMITS.description} characters`}
          />

          {/* Image Style Prompt Section */}
          <Box sx={{ mt: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Image Style Prompt
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Define a consistent visual style for AI-generated images. This Midjourney-style prompt will be applied when generating brand images.
            </Typography>

            {/* AI Generate Style Input */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Describe your desired style"
                placeholder="e.g., modern and minimalist, warm and friendly, bold and vibrant"
                value={stylePromptInput}
                onChange={(e) => setStylePromptInput(e.target.value)}
              />
              <LoadingButton
                variant="contained"
                startIcon={<AutoAwesome />}
                onClick={handleGenerateStylePrompt}
                loading={generatingStylePrompt}
                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
              >
                Generate Style
              </LoadingButton>
            </Box>

            {/* Style Prompt Output */}
            <TextField
              fullWidth
              label="Image Style Prompt"
              value={formData.imageStylePrompt}
              onChange={(e) =>
                setFormData({ ...formData, imageStylePrompt: e.target.value })
              }
              multiline
              rows={4}
              placeholder="clean minimalist corporate style, soft gradient backgrounds, professional photography aesthetic, cool blue and white color palette, soft diffused lighting, centered composition, modern and trustworthy mood --ar 16:9 --style raw --v 6"
              inputProps={{ maxLength: BRAND_IMAGE_FIELD_LIMITS.imageStylePrompt }}
              helperText={`${formData.imageStylePrompt?.length || 0}/${BRAND_IMAGE_FIELD_LIMITS.imageStylePrompt} characters`}
            />
          </Box>

          {/* Images Section */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Images ({formData.images.length})
            </Typography>

            {/* Upload Button */}
            <Box
              sx={{
                border: "2px dashed #ccc",
                borderRadius: 2,
                p: 2,
                textAlign: "center",
                mb: 2,
              }}
            >
              <ImageIcon sx={{ fontSize: 48, color: "#ccc", mb: 1 }} />
              <Typography variant="body2" gutterBottom>
                Upload brand images or select from library
              </Typography>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mt: 1, flexWrap: "wrap" }}>
                <LoadingButton
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUpload />}
                  loading={uploading}
                >
                  {formData.images.length > 0
                    ? "Upload More"
                    : "Upload Images"}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                  />
                </LoadingButton>
                <LoadingButton
                  variant="contained"
                  startIcon={<Collections />}
                  onClick={() => setImagePickerOpen(true)}
                >
                  Media Library & AI
                </LoadingButton>
              </Box>
            </Box>

            {/* Image Grid */}
            {formData.images.length > 0 && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {formData.images.map((image, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="150"
                        image={image.url}
                        alt={`Image ${index + 1}`}
                        sx={{
                          objectFit: "contain",
                          backgroundColor: "#f5f5f5",
                        }}
                      />
                      <Box sx={{ p: 1 }}>
                        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={image.type || ""}
                            label="Type"
                            onChange={(e) =>
                              handleImageTypeChange(index, e.target.value)
                            }
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {IMAGE_TYPES.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          fullWidth
                          size="small"
                          label="Usage Instructions"
                          placeholder="e.g., This is our brand model and they should be added as subject in every image"
                          value={image.description || ""}
                          onChange={(e) =>
                            handleImageDescriptionChange(
                              index,
                              e.target.value
                            )
                          }
                          multiline
                          rows={4}
                          sx={{ mb: 1 }}
                        />
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          {image.width} × {image.height}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveImage(index)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          {/* Templates Section */}
          <Box sx={{ mt: 3, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Template Overlays ({formData.templates.length})
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Upload PNG images with transparency to overlay on AI-generated images. Each template will create an additional version of every generated image.
            </Typography>

            {/* Template Upload/Create Buttons */}
            <Box
              sx={{
                border: "2px dashed",
                borderColor: "divider",
                borderRadius: 2,
                p: 2,
                textAlign: "center",
                mb: 2,
              }}
            >
              <ImageIcon sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
              <Typography variant="body2" gutterBottom>
                Upload PNG templates or create with AI
              </Typography>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mt: 1, flexWrap: "wrap" }}>
                <LoadingButton
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUpload />}
                  loading={uploading}
                >
                  {formData.templates.length > 0
                    ? "Upload More"
                    : "Upload PNG"}
                  <input
                    type="file"
                    hidden
                    accept="image/png"
                    multiple
                    onChange={handleTemplateUpload}
                  />
                </LoadingButton>
                <LoadingButton
                  variant="contained"
                  startIcon={<Brush />}
                  onClick={() => setFrameBuilderOpen(true)}
                >
                  Create with AI
                </LoadingButton>
              </Box>
            </Box>

            {/* Template Grid */}
            {formData.templates.length > 0 && (
              <Grid container spacing={2}>
                {formData.templates.map((template, index) => (
                  <Grid item xs={12} sm={6} md={4} key={template.id || index}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="120"
                        image={template.url}
                        alt={template.name || `Template ${index + 1}`}
                        sx={{
                          objectFit: "contain",
                          backgroundColor: "action.hover",
                        }}
                      />
                      <Box sx={{ p: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Template Name"
                          value={template.name || ""}
                          onChange={(e) =>
                            handleTemplateNameChange(index, e.target.value)
                          }
                          inputProps={{
                            maxLength: BRAND_IMAGE_FIELD_LIMITS.templateName,
                          }}
                          sx={{ mb: 1 }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Description"
                          placeholder="e.g., Instagram story frame"
                          value={template.description || ""}
                          onChange={(e) =>
                            handleTemplateDescriptionChange(
                              index,
                              e.target.value
                            )
                          }
                          inputProps={{
                            maxLength:
                              BRAND_IMAGE_FIELD_LIMITS.templateDescription,
                          }}
                          sx={{ mb: 1 }}
                        />
                        {template.width && template.height && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            {template.width} × {template.height}
                          </Typography>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveTemplate(index)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          {/* Image Models Selection */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Image Generation Models
            </Typography>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Select which AI models should use these images as references
            </Typography>
            <Alert severity="info" icon={<Info />} sx={{ mt: 2, mb: 2 }}>
              Only models tagged with <Chip label="Image Ref" size="small" sx={{ height: 20, mx: 0.5 }} /> support using uploaded reference images. Other models will use the style prompt and description to generate images in your brand style.
            </Alert>
            <Box sx={{ mt: 2 }}>
              {availableImageModels.map((model) => (
                <Box key={model.id} sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.imageModels.includes(model.id)}
                        onChange={() => handleModelToggle(model.id)}
                      />
                    }
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <span>{model.name}</span>
                          {model.credits && (
                            <Chip
                              label={`${model.credits} credits`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20 }}
                            />
                          )}
                          {model.tags?.map((tag: any) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              sx={{ height: 20 }}
                            />
                          ))}
                        </Box>
                        {model.description && (
                          <Typography variant="caption" color="text.secondary">
                            {model.description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </Box>
              ))}
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "flex-end" }}
          >
            <LoadingButton variant="outlined" onClick={onClose}>
              Cancel
            </LoadingButton>
            <LoadingButton
              variant="contained"
              onClick={handleSave}
              loading={loading || uploading}
              disabled={!formData.name}
            >
              {existingData ? "Update" : "Create"}
            </LoadingButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>

    {/* Frame Builder Dialog - rendered outside main dialog to avoid z-index issues */}
    <FrameBuilderDialog
      open={frameBuilderOpen}
      onClose={() => setFrameBuilderOpen(false)}
      companyId={companyId}
      companyName={company?.name || ""}
      getToken={getToken as () => Promise<string>}
      eventListener={eventListener as (event: any) => void}
      brandImages={formData.images}
      imageStylePrompt={formData.imageStylePrompt}
      onExport={(pngUrl, name, width, height) => {
        // Add the exported frame as a template
        const newTemplate = {
          id: uuidv4(),
          url: pngUrl,
          name: name,
          description: "Created with AI Frame Builder",
          width: width,
          height: height,
        };
        setFormData((prev) => ({
          ...prev,
          templates: [...prev.templates, newTemplate],
        }));
        setFrameBuilderOpen(false);
      }}
    />

    {/* Image Picker Dialog - for selecting from media library or generating with AI */}
    <ImagePicker
      open={imagePickerOpen}
      onClose={() => setImagePickerOpen(false)}
      onSelect={handleImagePickerSelect}
      getToken={getToken as () => Promise<string>}
      companyId={companyId}
      showAiGenerate={true}
      showApplyFrame={true}
    />
    </>
  );
};

export default BrandImageDialog;
